import { inject } from '@angular/core';
import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpInterceptorFn,
    HttpRequest,
    HttpResponse,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, Observable, Subject, takeUntil, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import * as Sentry from '@sentry/angular';

import { ExecutionResult, ExecutionStatus } from '../../proxy/models/common';
import { LoadingService } from '../services/loading.service';
import { AppInitService } from '../services/app-init.service';


export const httpRequestInterceptor: HttpInterceptorFn = (
    req: HttpRequest<any>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const router = inject(Router);
    const toast = inject(ToastrService);
    const loadingService = inject(LoadingService);
    const appInitService = inject(AppInitService);

    const destroy$ = new Subject<void>();

    const handleError = (error: HttpErrorResponse): Observable<never> => {
        if (error.status === 401) {
            toast.error(error.message);
            Sentry.captureException(new Error(error.message));
            setTimeout(() => {
                appInitService.removeConfig();
                window.location.reload();
            }, 2000);
        } else if (error.status === 404) {
            setTimeout(() => window.location.reload(), 2000);
        }

        return throwError(() => error);
    };

    return next(req).pipe(
        map((event: HttpEvent<unknown>): HttpEvent<unknown> => {
            if (event instanceof HttpResponse) {
                const result = (event.body as ExecutionResult)?.result;
                const firstError = result?.errors?.[0];

                if (result?.status === ExecutionStatus.failure) {
                    if ([1004, 1005].includes(firstError?.errorNumber)) {
                        destroy$.next();
                        destroy$.complete();
                        loadingService.hideLoader();
                        setTimeout(() => {
                            appInitService.removeConfig();
                            window.location.reload();
                        }, 2000);
                    } else if (firstError?.errorNumber === -1830258777) {
                        router.navigate(['/']);
                    }

                    toast.error(firstError?.message, '', { timeOut: 5000 });
                    Sentry.captureException(new Error(firstError?.message));
                } else if (result?.status === ExecutionStatus.warning) {
                    toast.warning(firstError?.message, '', { timeOut: 5000 });
                    Sentry.captureException(new Error(firstError?.message));
                }
            }

            return event;
        }),
        catchError((error: HttpErrorResponse) => handleError(error)),
        takeUntil(destroy$)
    );
};
